import React, { useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const SolarEnergyInfographic = () => {
  // Data voor de grafieken
  const dataGrowth = {
    labels: ['2019', '2020', '2021', '2022', '2023', '2024'],
    datasets: [{
      label: 'Geïnstalleerde Capaciteit (in GWp)',
      data: [1.7, 2.7, 3.2, 4.7, 5.0, 3.4],
      borderColor: 'hsl(var(--primary))',
      backgroundColor: 'hsl(var(--primary) / 0.2)',
      borderWidth: 3,
      pointBackgroundColor: 'hsl(var(--secondary))',
      pointBorderColor: 'hsl(var(--background))',
      pointRadius: 6,
      pointHoverRadius: 8,
      tension: 0.4,
      fill: true
    }]
  };

  const dataROI = {
    labels: [
      'Met salderen\n(tot 2027)', 
      'Zonder salderen\n(na 2027)', 
      'Met batterij\n(na 2027)'
    ],
    datasets: [{
      label: 'Geschatte Terugverdientijd (in jaren)',
      data: [6.5, 18, 10],
      backgroundColor: [
        'hsl(var(--accent))',
        'hsl(var(--destructive))', 
        'hsl(var(--primary))'
      ],
      borderColor: 'hsl(var(--background))',
      borderWidth: 2,
    }]
  };

  const dataDonut = {
    labels: ['Direct Eigen Verbruik', 'Teruglevering aan het Net', 'Verbruik van het Net'],
    datasets: [{
      label: 'Energiestromen Huishouden',
      data: [35, 45, 20],
      backgroundColor: [
        'hsl(var(--accent))',
        'hsl(var(--destructive))',
        'hsl(var(--primary))'
      ],
      hoverOffset: 4,
    }]
  };

  // Opties voor de grafieken
  const optionsCommon = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            family: 'Inter, system-ui, sans-serif',
          },
          color: 'hsl(var(--foreground))'
        }
      },
      tooltip: {
        backgroundColor: 'hsl(var(--popover))',
        titleColor: 'hsl(var(--popover-foreground))',
        bodyColor: 'hsl(var(--popover-foreground))',
        borderColor: 'hsl(var(--border))',
        borderWidth: 1,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'hsl(var(--border))',
        },
        ticks: {
          color: 'hsl(var(--muted-foreground))',
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: 'hsl(var(--muted-foreground))',
        }
      }
    },
  };

  const optionsDonut = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            family: 'Inter, system-ui, sans-serif',
          },
          color: 'hsl(var(--foreground))'
        }
      },
      tooltip: {
        backgroundColor: 'hsl(var(--popover))',
        titleColor: 'hsl(var(--popover-foreground))',
        bodyColor: 'hsl(var(--popover-foreground))',
        borderColor: 'hsl(var(--border))',
        borderWidth: 1,
        callbacks: {
          label: function(context: any) {
            let label = context.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed !== null) {
              label += context.parsed + '%';
            }
            return label;
          },
        }
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto bg-background rounded-3xl shadow-xl p-8 md:p-12 lg:p-16">
      {/* Intro Sectie */}
      <header className="text-center mb-12">
        <h1 className="text-4xl sm:text-5xl font-extrabold text-primary mb-4">
          Zonne-energie in Nederland: Actuele Trends
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed">
          De Nederlandse zonne-energiemarkt is voortdurend in beweging, gedreven door technologische vooruitgang, 
          overheidsbeleid en veranderende energieprijzen. Deze infographic visualiseert de meest recente 
          ontwikkelingen en biedt inzicht in wat de toekomst in petto heeft voor huishoudens met zonnepanelen.
        </p>
      </header>

      <hr className="border-t-2 border-accent my-12" />

      {/* Sectie 1: Marktgroei */}
      <section className="mb-12">
        <h2 className="text-3xl font-bold text-secondary mb-4 text-center">
          Spectaculaire Groei, Maar Nu Stabilisatie
        </h2>
        <p className="text-md text-muted-foreground mb-8 text-center max-w-2xl mx-auto">
          Na jaren van ongekende groei, waarin Nederland zich ontpopte tot wereldleider in zonne-energie per hoofd 
          van de bevolking, zien we nu een vertraging in de verkoop van nieuwe panelen. Desondanks blijft de totale 
          geïnstalleerde capaciteit gestaag toenemen.
        </p>
        <div className="bg-muted/50 p-6 rounded-2xl shadow-inner border border-border flex justify-center">
          <div className="relative h-96 w-full max-w-4xl">
            <Line data={dataGrowth} options={optionsCommon} />
          </div>
        </div>
      </section>

      <hr className="border-t-2 border-accent my-12" />

      {/* Sectie 2: De Toekomst van Salderen */}
      <section className="mb-12">
        <h2 className="text-3xl font-bold text-secondary mb-4 text-center">
          De Impact van de Salderingsregeling
        </h2>
        <p className="text-md text-muted-foreground mb-8 text-center max-w-2xl mx-auto">
          De politieke discussie over de salderingsregeling heeft voor onzekerheid gezorgd. Het plan om de regeling 
          af te bouwen is van de baan, maar per 1 januari 2027 stopt de regeling. Dit heeft directe financiële 
          gevolgen voor de terugverdientijd van je investering.
        </p>
        <div className="flex flex-col lg:flex-row lg:space-x-8">
          <div className="w-full lg:w-1/2 mb-8 lg:mb-0">
            <div className="bg-muted/50 p-6 rounded-2xl shadow-inner border border-border h-full flex flex-col items-center justify-center">
              <div className="relative h-96 w-full">
                <Bar data={dataROI} options={optionsCommon} />
              </div>
            </div>
          </div>
          <div className="w-full lg:w-1/2">
            <div className="bg-muted/50 p-6 rounded-2xl shadow-inner border border-border h-full">
              <h3 className="text-2xl font-semibold text-destructive mb-4 text-center">
                Scenario's voor Terugverdientijd
              </h3>
              <div className="flex flex-col space-y-4">
                <div className="bg-background p-4 rounded-xl shadow-md border-l-4 border-l-accent">
                  <h4 className="font-bold text-foreground">Huidige Salderingsregeling (tot 2027)</h4>
                  <p className="text-sm text-muted-foreground">
                    Terugverdientijd van 6-8 jaar. Je betaalt alleen voor de netto stroomafname.
                  </p>
                </div>
                <div className="bg-background p-4 rounded-xl shadow-md border-l-4 border-l-destructive">
                  <h4 className="font-bold text-foreground">Na 2027 (zonder salderen)</h4>
                  <p className="text-sm text-muted-foreground">
                    Terugverdientijd kan oplopen tot 15-20 jaar. Je krijgt alleen een lagere terugleververgoeding 
                    voor overtollige stroom.
                  </p>
                </div>
                <div className="bg-background p-4 rounded-xl shadow-md border-l-4 border-l-primary">
                  <h4 className="font-bold text-foreground">Na 2027 (met thuisbatterij)</h4>
                  <p className="text-sm text-muted-foreground">
                    Terugverdientijd verbetert aanzienlijk door eigen verbruik te maximaliseren. Een thuisbatterij 
                    kan de terugverdientijd verkorten naar 10-12 jaar.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="border-t-2 border-accent my-12" />

      {/* Sectie 3: Het Belang van Thuisgebruik */}
      <section className="mb-12">
        <h2 className="text-3xl font-bold text-secondary mb-4 text-center">
          Optimaliseer je Zonne-energie: Zelfverbruik!
        </h2>
        <p className="text-md text-muted-foreground mb-8 text-center max-w-2xl mx-auto">
          Met het aflopen van de salderingsregeling wordt het direct gebruiken van je eigen opgewekte stroom steeds 
          belangrijker. Door je verbruik te plannen, bespaar je meer en verminder je de belasting op het elektriciteitsnet.
        </p>
        <div className="bg-muted/50 p-6 rounded-2xl shadow-inner border border-border flex justify-center">
          <div className="relative h-96 w-full max-w-2xl">
            <Doughnut data={dataDonut} options={optionsDonut} />
          </div>
        </div>
      </section>

      <hr className="border-t-2 border-accent my-12" />

      {/* Conclusie */}
      <section className="text-center">
        <h2 className="text-3xl font-bold text-primary mb-4">De Toekomst van Zonnepanelen</h2>
        <p className="text-md text-muted-foreground mb-8 max-w-2xl mx-auto">
          Ondanks de beleidsveranderingen blijven zonnepanelen een slimme investering, zeker gezien de dalende prijzen. 
          De focus verschuift naar het optimaliseren van zelfverbruik en het overwegen van aanvullende technologieën 
          zoals thuisbatterijen. De zonne-energietransitie is een marathon, geen sprint, en de Nederlandse consument 
          blijft een cruciale rol spelen.
        </p>
      </section>
    </div>
  );
};

export default SolarEnergyInfographic;